import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Modal, Button, Input } from "reactstrap";
import { formattedNumber } from "../../../FormattedNumber/FormattedNumber";
import { NumberFormatInput } from "bx-react-ui/src/components/NumberFormatInput/NumberFormatInput";
import { useSelector } from "react-redux";
import { CircularProgress } from "bx-react-ui";
import { Icons } from "../../../UI/Icon/Icon";
import { getSavedPersonInfo, getCurrentAccount } from "../../../../reducers/selectors";
import { showToast } from "../../../../utils/helper";
import { updateFund, getPrechecks } from "../../../../services/api/inout";
import style from "./FundInvestModal.scss";
import { modes } from "../FundsAssetTable/FundsAssetTable";
import { Container } from "../../../UI/Container/Container";
import { BankAccountsSelector } from "../../../BankAccountsSelector/BankAccountsSelector";
import { fetchMainHolderBankAccounts, fetchAccountSellingPower } from "../../../../services/api/accounts";
import { getTexts } from "../../../../reducers/selectors";

export const FundInvestModal = ({ toggle, fund, mode }) => {
  const [value, setValue] = useState(fund.minAmmount);
  const texts = useSelector(getTexts);
  const [afterHoursModal, setAfterHoursModal] = useState({ show: false, accepted: false });
  const [loading, setLoading] = useState(false);
  const [loadingAccounts, setloadingAccounts] = useState(true);
  const [noFunds, setnoFunds] = useState(false);
  const currentAccount = useSelector(getCurrentAccount);
  const [activeAccount, setActiveAccount] = useState(null);
  const [errorMessage, setErrorMessage] = useState();
  const [bankAccounts, setBankAccounts] = useState([]);
  const [isAutoFullLiquidation, setIsAutoFullLiquidation] = useState(false);
  const [isFullLiquidation, setIsFullLiquidation] = useState(false);
  const [allBankAccountsAreInvalid, setAllBankAccountsAreInvalid] = useState(false);
  const [anyError, setAnyError] = useState(false);
  const [isScheduledRescue, setIsScheduledRescue] = useState(false);
  const tomorrowDate = new Date(new Date().setDate(new Date().getDate() + 1)).toISOString().slice(0, 10);
  const [processDate, setProcessDate] = useState(tomorrowDate);
  const [isProcessDateEligible, setIsProcessDateEligible] = useState(false);
  const [sellingpowerCash, setSellingpowerCash] = useState(-1);
  const personInfo = useSelector(getSavedPersonInfo);
  const [fields, setFields] = useState({
    AssetCouponRate: 0,
    SettlementCurrency: "",
    DebitTotal: 0,
    Commission: 0,
    BuyTotal: 0,
    Charges: 0,
    IVA: 0,
    Available: 0,
  });
  const maxPartialRedemptionPercentage = fund.maxPartialRedemptionPercentage || 100;
  useEffect(() => {
    if (
      mode === "SELL" &&
      !isFullLiquidation &&
      value < parseFloat(fund.AssetInformation.availableBalance) &&
      value > fund.minAmmount &&
      (+value * 100) / parseFloat(fund.AssetInformation.availableBalance) > maxPartialRedemptionPercentage
    ) {
      setIsAutoFullLiquidation(() => true);
    } else {
      setIsAutoFullLiquidation(() => false);
    }
    setErrorMessage(() => {
      const decimals = fund.AssetInformation.CurrencyISO == "USD" ? 2 : 0;
      if (value === 0 && !isFullLiquidation) {
        return "La cantidad ingresada no puede ser cero.";
      }
      if (value < 0 && !isFullLiquidation) {
        return "La cantidad ingresada no puede ser negativa.";
      }
      if (value < fund.minAmmount) {
        return `La cantidad ingresada no puede ser menor al mínimo permitido de ${formattedNumber(
          fund.minAmmount,
          decimals,
          decimals,
        )}.`;
      }
      if (mode === modes.BUY) {
        if (+fund.maxAmmount !== 0 && value > fund.maxAmmount) {
          return `La cantidad ingresada no puede ser mayor al máximo permitido de ${formattedNumber(
            fund.maxAmmount,
            decimals,
            decimals,
          )}.`;
        }
      }
    });
  }, [value, isFullLiquidation]);

  useEffect(() => {
    if (!currentAccount.IsPhysical && texts.tooltips.juridicalBankAccounts) {
      getMainHolderBankAccounts();
    } else {
      if (personInfo.PersonBankAccount) {
        setBankAccounts(personInfo.PersonBankAccount);
      }
    }
  }, [personInfo, currentAccount]);

  useEffect(() => {
    setIsProcessDateEligible(fund.isProcessDateEligible);
  }, [fund.isProcessDateEligible]);

  useEffect(() => {
    if (mode === modes.SELL) {
      fetchAccountSellingPower({ accountId: currentAccount.id, assetId: fund.AssetInformation.AssetId }).then(
        (response) => {
          setSellingpowerCash(response.response.sellingpowerCash);
        },
      );
    }

    if (!fields.SettlementCurrency) {
      const defaultCurrency = fund.AssetInformation.SettlementCurrencies.find(
        (settlementCurrenciesItem) => settlementCurrenciesItem.SettlementCurrencyIsDefault,
      );
      setFields((prevFields) => ({
        ...prevFields,
        SettlementCurrency: defaultCurrency.SettlementCurrencyISO,
        SettlementCurrencyId: defaultCurrency.SettlementCurrencyId,
      }));
    }
  }, []);

  useEffect(() => {
    if (fields.Available === 0) {
      const available = fund.OfferQuantitytoClient - fund.OfferQuantityTakenByClient;
      setFields((prevFields) => ({
        ...prevFields,
        Available: available,
      }));
    }
    if (fields.AssetCouponRate === 0) {
      fund.AssetInformation.Payments.map((paymentItem) => {
        const paymentItemAssetCouponDate = new Date(paymentItem.AssetCouponDate);
        if (new Date() < paymentItemAssetCouponDate) {
          setFields((prevFields) => ({
            ...prevFields,
            AssetCouponRate: +paymentItem.AssetCouponRate,
          }));
          return;
        }
      });
    }
  }, [fund]);

  const handleCheckboxChange = (event) => {
    setIsScheduledRescue(event.target.checked);
  };

  const executePrechecks = async () => {
    try {
      const { response } = await getPrechecks({
        order: {
          OrderSide: mode === modes.BUY ? "Buy" : "Sell",
          OrderSettlementCurrencyId: fields.SettlementCurrencyId,
          AssetId: fund.AssetInformation.AssetId,
          AssetTicker: fund.AssetInformation.AssetTicker,
          AssetClassForm: fund.AssetInformation.AssetClassForm,
          OrderQuantityIsCash: true,
          OrderInitDate: new Date().toISOString(),
          cashdiffered: true,
          CurrentAccount: {
            Precheck: {
              Total: +value,
            },
            HolderId: personInfo.PersonId,
            AccountId: currentAccount.id,
            clientBankAccount: activeAccount ? activeAccount.PersonBankAccountNumber : null,
            IsFullLiquidation: isFullLiquidation,
            Quantity: +value,
          },
          OrderProcessDate: isScheduledRescue ? processDate : new Date().toISOString(),
        },
      });

      if (response.result.Code !== 200) {
        showToast(response.result.Description, true);
        return false;
      }
      return true;
    } catch (err) {
      console.error(err);
      showToast("Error comprobando la inversión", true);
      return false;
    }
  };

  const validate = async () => {
    const decimals = fund.AssetInformation.CurrencyISO == "USD" ? 2 : 0;
    if (value === 0 && !isFullLiquidation) {
      showToast("La cantidad ingresada no puede ser cero.", true);
      return false;
    }
    if (value < 0 && !isFullLiquidation) {
      showToast("La cantidad ingresada no puede ser negativa.", true);
      return false;
    }
    if (value < fund.minAmmount) {
      showToast(
        `La cantidad ingresada no puede ser menor al mínimo permitido de ${formattedNumber(
          fund.minAmmount,
          decimals,
          decimals,
        )}.`,
        true,
      );
      return false;
    }
    if (+fund.maxAmmount !== 0 && value > fund.maxAmmount) {
      showToast(
        `La cantidad ingresada no puede ser mayor al máximo permitido de ${formattedNumber(
          fund.maxAmmount,
          decimals,
          decimals,
        )}.`,
        true,
      );
      return false;
    }
    if (!activeAccount) {
      showToast("Debe seleccionar una cuenta", true);
      return false;
    }

    if (mode === modes.SELL && sellingpowerCash > -1 && sellingpowerCash < value) {
      showToast("Error enviando la solicitud de rescate", true);
      return false;
    }

    const prechecks = await executePrechecks();
    if (!prechecks) return false;

    return true;
  };

  const confirmFund = (payload) => {
    setLoading(() => true);
    updateFund(payload)
      .then(({ response }) => response)
      .then(({ result }) => {
        if (result.Code !== 200) {
          if (result.Description == "Error generating order. Process date must be after the current day.") {
            showToast("La fecha debe ser un día hábil a partir de mañana", true);
          } else {
            showToast(
              `Error enviando la solicitud de ${mode === modes.BUY ? "suscripción" : "rescate"}.`,
              true,
            );
          }
          console.error(result.Description);
        } else {
          showToast(`Se envió la solicitud de ${mode === modes.BUY ? "suscripción" : "rescate"}.`);
          toggle();
        }
      })
      .catch((err) => {
        showToast(`Error enviando la solicitud de ${mode === modes.BUY ? "suscripción" : "rescate"}.`, true);
        console.error(err);
      })
      .finally(() => {
        setLoading(() => false);
      });
  };

  const getMainHolderBankAccounts = async () => {
    try {
      if (currentAccount && currentAccount.id) {
        const { response } = await fetchMainHolderBankAccounts(currentAccount.id);

        if (response && response.bankAccounts) {
          setBankAccounts(response.bankAccounts);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const onConfirm = async () => {
    setLoading(() => true);
    const validated = await validate();

    if (validated) {
      setLoading(() => true);

      confirmFund({
        fund: true,
        orderStatus: "Parked",
        accountId: currentAccount.id,
        bankAccountNumber: activeAccount.PersonBankAccountNumber,
        orderSide: mode === modes.BUY ? "Buy" : "Sell",
        assetId: fund.AssetInformation.AssetId,
        processDate: isScheduledRescue ? processDate : null,
        ammount: value,
        isFullLiquidation:
          isFullLiquidation ||
          (value * 100) / parseFloat(fund.AssetInformation.availableBalance) > maxPartialRedemptionPercentage,
      });
    } else {
      setLoading(() => false);
    }
  };

  const handleChange = (event) => {
    setValue(getNumber(event));
    setActiveAccount(null);
  };

  const changeDateHandler = (event) => {
    event.target.id === "processDate" ? setProcessDate(event.target.value) : null;
  };

  const toggleFullLiquidation = () => {
    setIsFullLiquidation((prev) => !prev);
  };

  const setAllBankAccountAreInvalid = (allAreInvalid, notEnoughFunds) => {
    setnoFunds(notEnoughFunds || noFunds);
    setAllBankAccountsAreInvalid(allAreInvalid);
  };

  const setLoadingStates = (isLoading) => {
    setloadingAccounts(isLoading);
  };

  const handleSetAnyError = () => {
    setAnyError(() => true);
  };

  const NoBankAccountsAvailable = () => {
    return (
      <div style={{ textAlign: "center", marginTop: "15px" }}>
        <span style={{ fontSize: "16px", fontWeight: "bold" }}>No tienes cuentas de banco disponibles</span>
      </div>
    );
  };

  return (
    <Modal isOpen={open} className={style.modalWrapper}>
      <Container className={style.sellContainer}>
        <span className={style.closeIcon} onClick={toggle}>
          <Icons.Close />
        </span>
        <span className={style.sellAssetName}>{fund.AssetInformation.AssetName}</span>
        <span className={style.sellTitle}>{`¿Cuánto querés ${
          mode === modes.BUY ? "suscribir" : "rescatar"
        }?`}</span>
        <hr className={style.divider} />
        <span className={style.qtyLabel}>Monto</span>
        <NumberFormatInput
          type="text"
          className={style.qtyInput}
          value={isFullLiquidation ? "Rescate total" : value}
          invalid={
            isFullLiquidation
              ? false
              : (mode === modes.BUY && value < +fund.minAmmount) ||
                (mode === modes.BUY && (+fund.maxAmmount === 0 ? false : value > fund.maxAmmount))
          }
          onValueChange={handleChange}
          decimalScale={fund.AssetInformation.CurrencyISO == "USD" ? 2 : 0}
          dataTypeDecimalName="price"
          placeholder={
            isFullLiquidation
              ? formattedNumber(
                  fund.AssetInformation.availableBalance,
                  fund.AssetInformation.CurrencyISO == "USD" ? 2 : 0,
                  fund.AssetInformation.CurrencyISO == "USD" ? 2 : 0,
                )
              : "Monto"
          }
          disabled={isFullLiquidation}
        />
        {isAutoFullLiquidation && (
          <span className={style.errorMessage}>
            El monto ingresado supera el {Math.floor(maxPartialRedemptionPercentage)}% del saldo actual, se
            generará un rescate total
          </span>
        )}
        {!loadingAccounts &&
          allBankAccountsAreInvalid &&
          (noFunds ? (
            <div className={style.invalidInput}>Acreditá saldo en tu cuenta para continuar.</div>
          ) : (
            <div className={style.invalidInput}>No tenés una cuenta para realizar la operación.</div>
          ))}
        {errorMessage ? <div className={style.invalidInput}>{errorMessage}</div> : null}
        {mode === modes.SELL && (
          <div className={style.fullLiquidationWrapper}>
            <span className={style.checkbox} onClick={toggleFullLiquidation}>
              <input type="checkbox" checked={isFullLiquidation} onChange={() => null} />
              <span>Rescate total</span>
            </span>
          </div>
        )}
        {mode === modes.BUY || !isProcessDateEligible ? null : (
          <div className={style.fullLiquidationWrapper}>
            <span className={style.checkbox}>
              <input type="checkbox" checked={isScheduledRescue} onChange={handleCheckboxChange} />
              <span>Programar rescate</span>
            </span>
            {isScheduledRescue && (
              <div>
                <Input
                  type="date"
                  name="processDate"
                  id="processDate"
                  value={processDate}
                  onChange={changeDateHandler}
                />
              </div>
            )}
          </div>
        )}
        <div className={style.bankAccounts}>
          <p className={style.title}>Elegí una cuenta para la acreditación</p>
          {!bankAccounts.filter((account) => account.PersonBankAccountStatus === "Active").length ? (
            <NoBankAccountsAvailable />
          ) : (
            <BankAccountsSelector
              bankAccounts={bankAccounts}
              selectedBankAccount={activeAccount}
              onSelectBankAccount={setActiveAccount}
              setAllBankAccountAreInvalid={setAllBankAccountAreInvalid}
              setLoadingStates={setLoadingStates}
              filters={{
                currencyISO: fund.AssetInformation.CurrencyISO,
                amount: mode === modes.BUY ? +value : 0,
                mode,
              }}
              setAnyError={handleSetAnyError}
            />
          )}
        </div>
        {loading ? (
          <CircularProgress />
        ) : (
          <Button
            className={style.buttonSell}
            onClick={onConfirm}
            disabled={
              loading ||
              (!isFullLiquidation && !+value) ||
              (!isFullLiquidation && mode === modes.BUY && value < fund.minAmmount) ||
              (!isFullLiquidation &&
                mode === modes.BUY &&
                +fund.maxAmmount !== 0 &&
                value > fund.maxAmmount) ||
              (afterHoursModal.show && !afterHoursModal.accepted) ||
              !activeAccount
            }
          >
            {mode === modes.BUY ? "Suscribir" : "Rescatar"}
          </Button>
        )}
      </Container>
    </Modal>
  );
};

const getNumber = (event) => {
  if (isNaN(event.floatValue) || event.value.endsWith(".")) {
    return event.value;
  }

  return event.floatValue;
};
